"use client";

import Image from "next/image";
import { useRef } from "react";
import { useRouter } from "next/navigation";
import { ArrowLeft, ArrowRight } from "lucide-react";

const swipeImages = [
  "/styleai/Swipe1.jpg",
  "/styleai/Swipe2.jpg",
  "/styleai/Swipe3.jpg",
  "/styleai/Swipe4.jpg",
  "/styleai/Swipe5.jpg",
  "/styleai/Swipe6.jpg",
  "/styleai/Swipe7.jpg",
  "/styleai/Swipe8.jpg",
];

const brandTicker = [
  "KITH",
  "NIKE",
  "ADIDAS",
  "STÜSSY",
  "ZARA",
  "NEW BALANCE",
  "BAPE",
  "SSENSE",
  "PALACE",
  "H&M",
  "FEAR OF GOD",
  "OFF-WHITE",
  "GALLERY DEPT.",
  "DTLR",
  "SNIPES",
  "NORDSTROM",
  "SAKS FIFTH AVENUE",
  "NEIMAN MARCUS",
  "SNEAKER POLITICS",
  "PACSUN",
  "THE NORTH FACE",
  "JORDAN",
  "MR PORTER",
  "FARFETCH",
  "FOOT LOCKER",
  "UNIQLO",
  "FINISH LINE",
];

export default function SwipeDeckSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const scroll = (direction: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -380 : 380,
      behavior: "smooth",
    });
  };

  const repeatedBrands = [...brandTicker, ...brandTicker];

  return (
    <section className="relative overflow-hidden bg-black px-5 py-14 text-white">
      {/* TOP BRAND TICKER */}
      <div className="relative -mx-5 mb-14 overflow-hidden border-y border-[#f5c842]/65 bg-black py-4 shadow-[0_0_22px_rgba(245,200,66,0.18)]">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-black to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-black to-transparent" />

        <div className="flex w-max animate-[brandMarquee_52s_linear_infinite] items-center whitespace-nowrap">
          {repeatedBrands.map((brand, index) => (
            <div
              key={`${brand}-${index}`}
              className="flex items-center gap-5 pr-5"
            >
              <span
                className={`text-[22px] font-black uppercase tracking-[-0.03em] md:text-[28px] ${
                  index % 2 === 0 ? "text-[#f5c842]" : "text-white"
                }`}
              >
                {brand}
              </span>

              <span className="text-lg font-black text-[#f5c842]">•</span>
            </div>
          ))}
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl">
        {/* BRAND HEADER */}
        <div className="mb-12 text-center">
          <h2 className="text-[54px] font-black uppercase leading-[0.9] tracking-[-0.06em] text-[#f5c842] drop-shadow-[0_0_18px_rgba(245,200,66,0.28)] md:text-[92px] lg:text-[112px]">
            Find Your Brands
          </h2>

          <p className="mx-auto mt-6 max-w-[760px] text-base font-semibold leading-relaxed text-white/88 md:text-xl">
            Find outfit inspiration and shopping options from popular
            streetwear, luxury, and everyday fashion retailers.
          </p>
        </div>

        <button
          onClick={() => scroll("left")}
          aria-label="Scroll left"
          className="absolute left-[-24px] top-[61%] z-30 hidden h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-[#f5c842]/80 bg-black text-[#f5c842] shadow-[0_0_24px_rgba(245,200,66,0.42)] transition hover:scale-105 hover:border-[#f5c842] md:flex"
        >
          <ArrowLeft size={25} />
        </button>

        {/* SWIPE LABEL */}
        <div className="mb-5">
          <p className="text-lg font-black uppercase tracking-[0.28em] text-[#f5c842] md:text-xl">
            Swipe for inspiration
          </p>
        </div>

        {/* IMAGE SWIPER */}
        <div
          ref={scrollRef}
          className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-2 pl-1 pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {swipeImages.map((src, index) => (
            <div
              key={src}
              className="relative h-[360px] min-w-[260px] snap-start overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_0_22px_rgba(0,0,0,0.78)] md:h-[395px] md:min-w-[300px] lg:min-w-[315px]"
            >
              <Image
                src={src}
                alt={`FHITZ inspiration outfit ${index + 1}`}
                fill
                className="h-full w-full object-fill"
                sizes="(max-width: 768px) 260px, (max-width: 1024px) 300px, 315px"
                priority={index < 3}
              />
            </div>
          ))}
        </div>

        <button
          onClick={() => scroll("right")}
          aria-label="Scroll right"
          className="absolute right-[-24px] top-[61%] z-30 hidden h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-[#f5c842]/80 bg-black text-[#f5c842] shadow-[0_0_24px_rgba(245,200,66,0.42)] transition hover:scale-105 hover:border-[#f5c842] md:flex"
        >
          <ArrowRight size={25} />
        </button>

        {/* BOTTOM DISCLAIMER + CTA */}
        <div className="mt-8 grid items-center gap-6 md:grid-cols-[1fr_auto]">
          <p className="max-w-[430px] text-xs leading-relaxed text-white/46">
            FHITZ is independent and is not affiliated with, endorsed by, or
            sponsored by any listed brands or retailers. All trademarks belong
            to their respective owners.
          </p>

          <button
            onClick={() => router.push("/admin-access")}
            className="group flex items-center justify-center gap-4 rounded-xl border border-[#f5c842]/85 bg-black px-9 py-4 text-base font-black uppercase tracking-[0.22em] text-[#f5c842] shadow-[0_0_24px_rgba(245,200,66,0.28)] transition hover:scale-[1.02] hover:border-[#f5c842] hover:shadow-[0_0_34px_rgba(245,200,66,0.42)] md:min-w-[390px] md:text-lg"
          >
            Generate Your Fit
            <ArrowRight
              size={24}
              className="transition group-hover:translate-x-1"
            />
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes brandMarquee {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </section>
  );
}